import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, TrashIcon, EllipsisVerticalIcon, EyeIcon, EyeSlashIcon, DocumentDuplicateIcon } from '@heroicons/react/24/solid';
import ModalConfirm from '../../../moduleComponents/ModalConfirm';
import ModalBudgetDuplicate from './ModalBudgetDuplicate';
import { formatDate } from 'shared_items/formatConventions';
import CardBoxClassL1 from './CardBoxClassL1';
import CardBudgetAddL1 from './CardBudgetAddL1';
import { toast } from 'react-hot-toast';
import { createBudgetPlanItem } from '../../viewServices/budget_api';
import { getL1Options, getL2Options, buildL0Key, buildL1Key } from '../../../moduleServices/helper_hierarchicalCategory';

function totalOfBudgets(budgets) {
  if (!budgets.length) return 0;
  const type = budgets[0].budget_type;
  return budgets.reduce((acc, b) => acc + (+b.budget_amount), 0) * (type === 'In' ? 1 : -1);
}
function totalOfL1(l2s) {
  let sum = 0;
  Object.values(l2s).forEach(budgets => {
    if (budgets && budgets.length) sum += totalOfBudgets(budgets);
  });
  return sum;
}
function renderTotal(val) {
  const valor = Math.abs(val).toLocaleString('pt-BR', { style: 'decimal', minimumFractionDigits: 2 });
  
  if (val === 0) {
    return (
      <span className="text-gray-400 font-normal">
        {valor}
      </span>
    );
  }
  
  return (
    <span className={val > 0 ? 'text-green-700 font-bold' : 'text-red-700 font-bold'}>
      {val > 0 ? '+ ' : '- '}{valor}
    </span>
  );
}

function getL1GroupsForType(inputGroups, type, hierarchicalCategories, l0Label, date) {
  if (!inputGroups) return [];
  
  const l0Key = buildL0Key(l0Label, type);
  
  return Object.entries(inputGroups)
    .map(([l1Label, l2s]) => {
      const filteredL2Budgets = {};
      Object.entries(l2s).forEach(([l2Id, budgets]) => {
        const onlyType = budgets.filter(b => b.budget_type === type);
        if (onlyType.length) filteredL2Budgets[l2Id] = onlyType;
      });
      const l1Total = totalOfL1(filteredL2Budgets);
      
      const l1Key = buildL1Key(l0Label, type, l1Label);
      const l1CategoryObj = hierarchicalCategories?.by_l1?.[l1Key]
        ? { value: l1Key, label: hierarchicalCategories.by_l1[l1Key].name }
        : { value: l1Key, label: l1Label };
      
      return {
        l1Id: l1Key,
        l1Category: l1CategoryObj,
        filteredL2Budgets,
        l1Total,
        hierarchicalCategories,
        l0Id: l0Key,
        date,
      };
    })
    .filter(grp => Object.values(grp.filteredL2Budgets).flat().length > 0);
}

function renderBudgetSection(type, l1Groups, props, isVisibleL2, planId) {
  const sectionTotal = l1Groups.reduce((acc, g) => acc + totalOfL1(g.filteredL2Budgets), 0);
  const isIn = type === 'In';
  return l1Groups.length ? (
    <>
      <div className="flex items-center justify-between mb-1 ml-1 mr-1">
        <span className="text-xs font-semibold text-gray-400">{type}</span>
        {sectionTotal !== 0 && (
          <span className={`text-xs font-semibold ${isIn ? 'text-green-700' : 'text-red-600'}`}>
            {isIn ? '+ ' : '- '}{Math.abs(sectionTotal).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          </span>
        )}
      </div>
      {l1Groups.map(l1Info => (
        <CardBoxClassL1
          key={type + '-' + l1Info.l1Id}
          planId={planId}
          l1Category={l1Info.l1Category}
          l1Total={renderTotal(l1Info.l1Total)}
          filteredL2Budgets={l1Info.filteredL2Budgets}
          budgetType={type}
          hierarchicalCategories={l1Info.hierarchicalCategories}
          onDeleteL1={props.onDeleteL1
            ? () => {
                const l1Label = l1Info.l1Category?.label || '';
                props.onDeleteL1(l1Label, type);
              }
            : undefined
          }
          onItemCreated={props.onItemCreated}
          onItemUpdated={props.onItemUpdated}
          onItemDeleted={props.onItemDeleted}
          l0Id={l1Info.l0Id}
          date={l1Info.date}
          isVisibleL2={isVisibleL2}
        />
      ))}
    </>
  ) : null;
}

const BudgetGroupedCard = ({
  planId,
  categL0,
  groups: inputGroups,
  onDeleteL1,
  onDeleteGroup,
  hierarchicalCategories,
  onItemCreated,
  onItemUpdated,
  onItemDeleted,
  onPlanDuplicated,
  date,
}) => {
  const [open, setOpen] = useState(false);
  const [showModalDeleteL0, setShowModalDeleteL0] = useState(false);
  const [showModalDuplicate, setShowModalDuplicate] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isVisibleL2, setIsVisibleL2] = useState(true);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = e => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  async function onCreateL1Block({ budget_type, class_l1, class_l2 }) {
    const newItem = {
      plan: planId,
      class_l0: categL0,
      class_l1,
      class_l2,
      budget_type,
      budget_amount: 0,
      description: '-',
      budget_currency: 'BRL',
    };
    try {
      const res = await createBudgetPlanItem(newItem);
      if (onItemCreated) onItemCreated(res?.data ?? null);
    } catch {
      toast.error('Failed to create budget.');
    }
  }

  const inGroups = getL1GroupsForType(inputGroups, 'In', hierarchicalCategories, categL0, date);
  const outGroups = getL1GroupsForType(inputGroups, 'Out', hierarchicalCategories, categL0, date);

  const inTotal = inGroups.reduce((acc, g) => acc + totalOfL1(g.filteredL2Budgets), 0);
  const outTotal = outGroups.reduce((acc, g) => acc + totalOfL1(g.filteredL2Budgets), 0);

  return (
    <div className="mb-2 transition-all bg-white rounded-2xl shadow flex flex-col">
      <div
        className="w-full flex flex-row justify-between items-center px-5 py-4 select-none rounded-t-2xl cursor-pointer transition hover:bg-green-50"
        style={{ minHeight: '56px' }}
        onClick={() => setOpen(x => !x)}
      >
        {/* Date and category L0 */}
        <div className="flex items-center gap-4">
          <span className="text-sm font-bold text-gray-700">
            {date ? formatDate(date, 'chart') : ''}
          </span>
          <span className="font-bold text-green-900 text-lg leading-6">{categL0}</span>
        </div>
        <div className="flex gap-1.5 items-center ml-4">
          <div className="relative" ref={menuRef}>
          <button
              onClick={e => { e.stopPropagation(); setMenuOpen(x => !x); }}
              className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition"
              title="Open menu"
            tabIndex={-1}
          >
              <EllipsisVerticalIcon className="w-5 h-5 text-gray-500" />
            </button>
            {menuOpen && (
              <div
                className="absolute z-30 right-0 top-full mt-1 bg-white border border-gray-200 rounded shadow-lg p-1 w-max"
                onClick={e => e.stopPropagation()}
              >
                <button
                  className="flex items-center gap-2 text-gray-700 hover:bg-gray-100 px-3 py-1.5 rounded text-sm whitespace-nowrap"
                  onClick={() => { setMenuOpen(false); setShowModalDuplicate(true); }}
                >
                  <DocumentDuplicateIcon className="w-4 h-4" />Duplicate month
                </button>
                <button
                  className="flex items-center gap-2 text-red-600 hover:bg-red-50 px-3 py-1.5 rounded text-sm whitespace-nowrap"
                  onClick={() => { setMenuOpen(false); setShowModalDeleteL0(true); }}
                >
                  <TrashIcon className="w-4 h-4" />Delete group
                </button>
              </div>
            )}
          </div>
          <button
            tabIndex={-1}
            aria-label={open ? 'Collapse' : 'Expand'}
            onClick={e => { e.stopPropagation(); setOpen(x => !x); }}
            className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-green-100 transition"
          >
            <ChevronDownIcon className={`h-6 w-6 text-green-700 transition-transform ${open ? 'rotate-180' : 'rotate-0'}`} />
          </button>
        </div>
      </div>
      <ModalConfirm
        isOpen={showModalDeleteL0}
        onClose={() => setShowModalDeleteL0(false)}
        onConfirm={() => { setShowModalDeleteL0(false); onDeleteGroup && onDeleteGroup(planId, categL0); }}
        title="Delete entire group?"
        message={`Are you sure you want to delete all '${categL0}' budgets for ${date ? date.slice(0, 7) : 'this month'}? This action cannot be undone.`}
        confirmText="Delete"
      />
      <ModalBudgetDuplicate
        isOpen={showModalDuplicate}
        onClose={() => setShowModalDuplicate(false)}
        planId={planId}
        sourceDate={date}
        onSuccess={(newPlan) => { onPlanDuplicated && onPlanDuplicated(newPlan); }}
      />
      {open && (
        <div className="bg-white rounded-b-2xl shadow p-2 md:p-4">
          <CardBudgetAddL1
            l0Label={categL0}
            hierarchicalCategories={hierarchicalCategories}
            onCreateL1Block={onCreateL1Block}
            isVisibleL2={isVisibleL2}
            onToggleVisibility={() => setIsVisibleL2(x => !x)}
          />
          <div className="space-y-2">
            {renderBudgetSection('In', inGroups, { onDeleteL1: (l1Label, type) => onDeleteL1 && onDeleteL1(planId, categL0, l1Label, type), onItemCreated, onItemUpdated, onItemDeleted }, isVisibleL2, planId)}
            {renderBudgetSection('Out', outGroups, { onDeleteL1: (l1Label, type) => onDeleteL1 && onDeleteL1(planId, categL0, l1Label, type), onItemCreated, onItemUpdated, onItemDeleted }, isVisibleL2, planId)}
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetGroupedCard;
